// src/components/Browser/SignatureModal.js
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ethers } from 'ethers';
import { FaShieldAlt, FaExclamationTriangle, FaInfoCircle, FaCheck } from 'react-icons/fa';
import { WalletContext } from '../../contexts/WalletContext';
import FaviconImage from './FaviconImage';
import './SignatureModal.css';

const SignatureModal = ({
  isOpen,
  onClose,
  signatureRequest,
  onApprove,
  onReject,
  isLoading,
  site
}) => {
  const { t } = useTranslation();
  const { currentWallet } = useContext(WalletContext);
  const [messageContent, setMessageContent] = useState('');
  const [signatureType, setSignatureType] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);
  const [securityCheck, setSecurityCheck] = useState({
    isScam: false,
    isRisky: false,
    isValidDomain: true,
    message: ''
  });
  
  // Process signature request when it changes
  useEffect(() => {
    if (!signatureRequest) return;
    
    try {
      // Reset error state on new request
      setError(null);
      
      // Determine signature type
      const { method, params } = signatureRequest;
      setSignatureType(method);
      
      console.log('Processing signature request:', method, params);
      
      // Perform security checks
      performSecurityChecks(method, params, site);
      
      // Extract and format message based on method
      if (method === 'personal_sign') {
        const rawMessage = params[0]; // First parameter is the message
        
        // Check if the message is hex encoded
        if (ethers.isHexString(rawMessage)) {
          try {
            // Try to convert from hex to UTF-8
            const utf8Message = ethers.toUtf8String(rawMessage);
            setMessageContent(utf8Message);
          } catch (error) {
            console.error('Error converting hex to UTF-8:', error);
            setMessageContent(rawMessage); // Fallback to raw message
          }
        } else {
          setMessageContent(rawMessage);
        }
      } else if (method === 'eth_sign') {
        const rawMessage = params[1]; // Second parameter is the message in eth_sign
        
        // Handle hex message
        if (ethers.isHexString(rawMessage)) {
          try {
            const utf8Message = ethers.toUtf8String(rawMessage);
            setMessageContent(utf8Message);
          } catch (error) {
            console.error('Error converting hex to UTF-8:', error);
            setMessageContent(rawMessage);
          }
        } else {
          setMessageContent(rawMessage);
        }
        
        // eth_sign is considered less secure
        setSecurityCheck(prev => ({
          ...prev,
          isRisky: true,
          message: t('browser.signatureRequest.ethSignWarning', 'eth_sign is considered less secure than personal_sign')
        }));
      } else if (method === 'eth_signTypedData' || method === 'eth_signTypedData_v4') {
        // For typed data, we need to parse and format it nicely
        let typedData;
        
        // Determine which parameter contains the data (more flexible)
        let typedDataParam;
        if (Array.isArray(params)) {
          // For most implementations, typed data is in params[1]
          typedDataParam = params.length > 1 ? params[1] : params[0];
        } else {
          typedDataParam = params;
        }
        
        if (typeof typedDataParam === 'string') {
          // Parse JSON string if needed
          try {
            typedData = JSON.parse(typedDataParam);
          } catch (error) {
            console.error('Error parsing typed data:', error);
            setMessageContent(typedDataParam);
            setError('Error parsing typed data: Invalid JSON format');
            return;
          }
        } else {
          typedData = typedDataParam;
        }
        
        // Validate typed data structure
        if (!typedData || !typedData.domain || !typedData.types || !typedData.message) {
          console.error('Invalid typed data structure:', typedData);
          setError('Invalid typed data: missing domain, types, or message');
          setMessageContent(JSON.stringify(typedData, null, 2));
          return;
        }
        
        // Check for potential permission requests in the typed data
        checkForPermissionRequests(typedData);
        
        // Format the typed data for display
        try {
          const formattedData = JSON.stringify(typedData, null, 2);
          setMessageContent(formattedData);
        } catch (error) {
          console.error('Error formatting typed data:', error);
          setMessageContent(JSON.stringify(typedData));
        }
      }
    } catch (error) {
      console.error('Error processing signature request:', error);
      setError(`Error processing signature request: ${error.message}`);
      setMessageContent(t('browser.signatureRequest.errorProcessing'));
    }
  }, [signatureRequest, t]);
  
  // Perform security checks on the signature request
  const performSecurityChecks = (method, params, siteUrl) => {
    // Reset security checks
    const securityState = {
      isScam: false,
      isRisky: false,
      isValidDomain: true,
      message: ''
    };
    
    // Check if this is eth_sign (considered less secure)
    if (method === 'eth_sign') {
      securityState.isRisky = true;
      securityState.message = t('browser.signatureRequest.ethSignWarning', 'eth_sign is considered less secure than personal_sign');
    }
    
    // Check domain validity
    if (siteUrl) {
      try {
        const url = new URL(siteUrl);
        // Add any domain validation logic here
      } catch (error) {
        securityState.isValidDomain = false;
        securityState.message = t('browser.signatureRequest.invalidDomain', 'Invalid domain');
      }
    }
    
    // Check for malicious phrases in the message
    const checkMessage = Array.isArray(params) ? 
      params.find(p => typeof p === 'string') : 
      (typeof params === 'string' ? params : '');
    
    if (checkMessage) {
      const lowerMessage = checkMessage.toLowerCase();
      // Check for common scam phrases
      const scamPhrases = ['free', 'airdrop', 'claim your', 'urgently', 'only today'];
      if (scamPhrases.some(phrase => lowerMessage.includes(phrase))) {
        securityState.isRisky = true;
        securityState.message = t('browser.signatureRequest.suspiciousContent', 'This signature request contains suspicious content');
      }
    }
    
    // Update security check state
    setSecurityCheck(securityState);
  };
  
  // Check for potentially dangerous permission requests in typed data
  const checkForPermissionRequests = (typedData) => {
    if (!typedData) return;
    
    // Check domain name for trusted sources
    const domain = typedData.domain?.name?.toLowerCase() || '';
    
    // Check for unlimited token approvals in the message data
    const message = typedData.message || {};
    const hasUnlimitedValue = Object.values(message).some(value => {
      if (typeof value === 'string' && value.includes('ffffffffffffffffffffffffffffffffffffffff')) {
        return true;
      }
      return false;
    });
    
    if (hasUnlimitedValue) {
      setSecurityCheck(prev => ({
        ...prev,
        isRisky: true,
        message: t('browser.signatureRequest.unlimitedApproval', 'This request may include an unlimited token approval')
      }));
    }
  };
  
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onReject();
      }
    };
    
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onReject]);
  
  // Handle approval with error handling
  const handleApprove = async () => {
    try {
      // Reset error state
      setError(null);
      
      // Call the onApprove callback
      await onApprove();
    } catch (err) {
      console.error('Error approving signature:', err);
      setError(`Error approving signature: ${err.message}`);
    }
  };
  
  // Helper to get a readable signature type
  const getReadableSignatureType = () => {
    switch (signatureType) {
      case 'personal_sign':
        return t('browser.signatureRequest.personalSign', 'Personal Sign');
      case 'eth_sign':
        return t('browser.signatureRequest.ethSign', 'Ethereum Sign');
      case 'eth_signTypedData':
        return t('browser.signatureRequest.typedData', 'Typed Data (EIP-712)');
      case 'eth_signTypedData_v4':
        return t('browser.signatureRequest.typedDataV4', 'Typed Data v4 (EIP-712)');
      default:
        return signatureType;
    }
  };
  
  // Helper to determine if this signature method has security risks
  const isRiskySignature = () => {
    // eth_sign is considered less secure than personal_sign
    return signatureType === 'eth_sign' || securityCheck.isRisky;
  };
  
  // Handle modal click to prevent overlay click propagation
  const handleModalClick = (e) => {
    e.stopPropagation();
  };
  
  if (!isOpen || !signatureRequest) return null;
  
  // Format address for display
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };
  
  // Extract hostname safely
  let hostname = '';
  try {
    hostname = site ? new URL(site).hostname : '';
  } catch (e) {
    hostname = site || '';
  }
  
  return (
    <div className="signature-modal-overlay" onClick={onReject}>
      <div className="signature-modal" onClick={handleModalClick}>
        <div className="signature-modal-header">
          <FaShieldAlt className="signature-icon" />
          <h2>{t('browser.signatureRequest.title', 'Signature Request')}</h2>
          <button className="close-button" onClick={onReject}>×</button>
        </div>
        
        <div className="signature-modal-content">
          <div className="site-info">
            <div className="site-favicon">
              <FaviconImage hostname={hostname} className="site-favicon-img" alt={hostname} />
            </div>
            <div className="site-details">
              <h3>{hostname}</h3>
              <p className="site-url">{site}</p>
            </div>
          </div>
          
          <div className="signature-warning">
            <FaExclamationTriangle className="warning-icon" />
            <p>{t('browser.signatureRequest.warning', 'You are about to sign a message with your Ethereum account')}</p>
          </div>
          
          {isRiskySignature() && (
            <div className="signature-warning risky">
              <FaExclamationTriangle className="warning-icon" />
              <p>{securityCheck.message || t('browser.signatureRequest.riskyWarning', 'This signature method has security risks. Proceed with caution.')}</p>
            </div>
          )}
          
          {error && (
            <div className="signature-warning risky">
              <FaExclamationTriangle className="warning-icon" />
              <p>{error}</p>
            </div>
          )}
          
          <div className="signature-details">
            <h4>{t('browser.signatureRequest.details', 'Signature Details')}</h4>
            
            <div className="signature-detail-item">
              <span className="detail-label">{t('browser.signatureRequest.type', 'Type')}</span>
              <span className="detail-value">{getReadableSignatureType()}</span>
            </div>
            
            <div className="signature-detail-item">
              <span className="detail-label">{t('browser.signatureRequest.from', 'From')}</span>
              <span className="detail-value">{formatAddress(currentWallet?.address)}</span>
            </div>
            
            <div className="signature-message-container">
              <div className="signature-message-header">
                <span className="detail-label">{t('browser.signatureRequest.message', 'Message')}</span>
                <button 
                  className="expand-button" 
                  onClick={() => setExpanded(!expanded)}
                >
                  {expanded ? t('browser.signatureRequest.collapse', 'Collapse') : t('browser.signatureRequest.expand', 'Expand')}
                </button>
              </div>
              <pre className={`signature-message ${expanded ? 'expanded' : ''}`}>
                {messageContent}
              </pre>
            </div>
          </div>
          
          <div className="signature-info">
            <FaInfoCircle className="info-icon" />
            <p>{t('browser.signatureRequest.signingInfo', 'By signing, you are confirming that you have read the message and approve the signature request. This does not cost any gas fees.')}</p>
          </div>
          
          <div className="signature-actions">
            <button 
              className="reject-button"
              onClick={onReject}
              disabled={isLoading}
            >
              {t('browser.signatureRequest.reject', 'Reject')}
            </button>
            <button 
              className="approve-button"
              onClick={handleApprove}
              disabled={isLoading || securityCheck.isScam}
            >
              {isLoading ? (
                <span className="loading-spinner"></span>
              ) : null}
              {t('browser.signatureRequest.sign', 'Sign')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignatureModal;